import { useEffect, useRef, useState } from "react";
import { X, Mail, Bold, Italic, Underline, List, ListOrdered, Link2, RemoveFormatting, Save, Unlink } from "lucide-react";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "./ConfirmModal";
import { SIGNATURE_SANITIZE_CONFIG } from "../lib/emailSanitize";

/**
 * Gmail integration settings side-panel.
 *
 * Houses everything post-connection:
 *  - Connected account + last sync info
 *  - HTML email signature editor (auto-appended to outgoing email)
 *  - Disconnect action (with confirm)
 *
 * Props:
 *  - open (bool)
 *  - onClose ()
 *  - account: { email, last_sync_at } from /integrations/status
 *  - onDisconnected(): parent should refresh status
 *  - autoFocusSignature (bool): if true, editor focuses on open (celebration CTA path)
 */
// Email signatures are HTML fragments — almost always table-based with inline
// styles and sized images. See lib/emailSanitize.js for the shared allowlist
// (used both here and in ComposeEmailModal so the preview matches the save).

export default function GmailSettingsModal({ open, onClose, account, onDisconnected, autoFocusSignature = false }) {
  const { user, refresh } = useAuth();
  const confirm = useConfirm();
  const editorRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showConfirmDisconnect, setShowConfirmDisconnect] = useState(false);

  const initialSig = user?.email_signature || "";

  useEffect(() => {
    if (!open) return;
    if (editorRef.current) {
      // Initial signature can be HTML or plain text — render both correctly.
      const html = initialSig && !/<\w+/.test(initialSig)
        ? initialSig.replace(/\n/g, "<br>")
        : initialSig;
      editorRef.current.innerHTML = DOMPurify.sanitize(html, SIGNATURE_SANITIZE_CONFIG);
    }
    setDirty(false);
    setShowConfirmDisconnect(false);
    if (autoFocusSignature) {
      setTimeout(() => editorRef.current?.focus(), 120);
    }
  }, [open, initialSig, autoFocusSignature]);

  if (!open) return null;

  const exec = (cmd, value) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
    setDirty(true);
  };

  const insertLink = async () => {
    const url = await confirm({
      title: "Insert link",
      input: { placeholder: "https://", defaultValue: "https://", required: true, label: "Link URL" },
      confirmLabel: "Insert",
    });
    if (url) exec("createLink", url);
  };

  const save = async () => {
    const raw = editorRef.current?.innerHTML || "";
    const cleaned = DOMPurify.sanitize(raw, SIGNATURE_SANITIZE_CONFIG);
    setSaving(true);
    try {
      await api.patch("/auth/me/profile", { email_signature: cleaned });
      if (refresh) await refresh();
      toast.success("Signature saved · will be added to every email you send from Pragma");
      setDirty(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save signature");
    }
    setSaving(false);
  };

  const disconnect = async () => {
    setDisconnecting(true);
    try {
      await api.post("/integrations/google/disconnect");
      toast.success("Gmail disconnected");
      onDisconnected?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to disconnect");
    }
    setDisconnecting(false);
  };

  const lastSync = account?.last_sync_at
    ? new Date(account.last_sync_at).toLocaleString()
    : "Not synced yet";

  return (
    <>
      <div className="side-panel-backdrop" onClick={() => !saving && !disconnecting && onClose()} />
      <div className="side-panel" data-testid="gmail-settings-panel">
        {/* Header */}
        <div className="px-6 py-5" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div style={{
                width: 40, height: 40, borderRadius: 10, background: "rgba(234,67,53,0.10)",
                color: "#EA4335", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <Mail size={20} />
              </div>
              <div>
                <h2 className="text-xl">Gmail settings</h2>
                <div className="mono text-[11px] text-mute mt-1">
                  {account?.email} · last sync {lastSync}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid="gmail-settings-close"
              className="pg-icon-btn"
              style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, color: "var(--pg-text-mute)" }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Signature editor */}
          <section>
            <div className="mb-2">
              <label className="block mono text-[11px] text-mute uppercase tracking-wider">Email signature</label>
              <p className="text-sm mt-1" style={{ color: "var(--pg-text-mute)" }}>
                Automatically appended to every email you send from Pragma via Gmail.
                Rich formatting is supported.
              </p>
            </div>

            <div className="rte-toolbar" data-testid="gmail-sig-toolbar">
              <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("bold"); }} title="Bold" data-testid="gmail-sig-bold"><Bold size={14} /></button>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("italic"); }} title="Italic" data-testid="gmail-sig-italic"><Italic size={14} /></button>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("underline"); }} title="Underline" data-testid="gmail-sig-underline"><Underline size={14} /></button>
              <span className="rte-sep" />
              <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("insertUnorderedList"); }} title="Bullet list" data-testid="gmail-sig-ul"><List size={14} /></button>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("insertOrderedList"); }} title="Numbered list" data-testid="gmail-sig-ol"><ListOrdered size={14} /></button>
              <span className="rte-sep" />
              <button type="button" onMouseDown={(e) => { e.preventDefault(); insertLink(); }} title="Insert link" data-testid="gmail-sig-link"><Link2 size={14} /></button>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("removeFormat"); }} title="Clear formatting" data-testid="gmail-sig-clear"><RemoveFormatting size={14} /></button>
            </div>

            <div
              ref={editorRef}
              className="rte-editor"
              contentEditable
              suppressContentEditableWarning
              onInput={() => setDirty(true)}
              data-placeholder="Jane Doe · Head of Sales · Acme Co · jane@acme.com · +1 (555) 123-4567"
              data-testid="gmail-sig-editor"
              style={{ minHeight: 140 }}
            />

            <div className="mt-3 flex justify-end">
              <button
                type="button"
                className="pg-btn pg-btn-primary"
                onClick={save}
                disabled={saving || !dirty}
                data-testid="gmail-sig-save"
              >
                <Save size={14} strokeWidth={2.2} />
                {saving ? "Saving…" : "Save signature"}
              </button>
            </div>
          </section>

          {/* Sync info */}
          <section style={{ borderTop: "1px solid var(--pg-border)", paddingTop: 20 }}>
            <label className="block mono text-[11px] text-mute uppercase tracking-wider mb-1">Sync</label>
            <p className="text-sm" style={{ color: "var(--pg-text-mute)" }}>
              Incoming and outgoing emails between you and any contact, lead, deal or company
              are automatically pulled into their History timeline. Sync runs every 5 minutes.
              We never touch personal correspondence with addresses that aren&apos;t already in your CRM.
            </p>
          </section>

          {/* Danger zone */}
          <section style={{ borderTop: "1px solid var(--pg-border)", paddingTop: 20 }}>
            <label className="block mono text-[11px] text-mute uppercase tracking-wider mb-2">Danger zone</label>
            {!showConfirmDisconnect ? (
              <button
                type="button"
                className="pg-btn"
                onClick={() => setShowConfirmDisconnect(true)}
                data-testid="gmail-settings-disconnect"
                style={{ color: "var(--pg-red, #d93025)", borderColor: "var(--pg-red, #d93025)" }}
              >
                <Unlink size={13} strokeWidth={2.2} /> Disconnect Gmail
              </button>
            ) : (
              <div style={{
                background: "rgba(217,48,37,0.05)",
                border: "1px solid rgba(217,48,37,0.2)",
                borderRadius: 8,
                padding: 14,
              }}>
                <p className="text-sm mb-3" style={{ color: "var(--pg-text)" }}>
                  Disconnect Gmail? Emails already synced will stay in the timeline,
                  but no new mail will be pulled and you won&apos;t be able to send from Pragma.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="pg-btn"
                    onClick={() => setShowConfirmDisconnect(false)}
                    disabled={disconnecting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="pg-btn"
                    onClick={disconnect}
                    disabled={disconnecting}
                    data-testid="gmail-settings-disconnect-confirm"
                    style={{ background: "var(--pg-red, #d93025)", color: "white", borderColor: "var(--pg-red, #d93025)" }}
                  >
                    {disconnecting ? "Disconnecting…" : "Yes, disconnect"}
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
}
